import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ContentCutRoundedIcon from "@mui/icons-material/ContentCutRounded";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { AccentIconTile } from "../../../components/UIToolkit/molecules/AccentIconTile";
import { ICON_SIZE } from "../../../theme/tokens";
import { PBJ_ALPHA } from "./colors";
import { type CumulativeItemResult, DAYS_PER_WEEK_FULL, DAYS_PER_WEEK_WEEKEND } from "./cumulativeScoring.utils";
import { WEEKEND_REC_IDS } from "./planOutcome.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import type { PlanItem } from "./recommendations.constants";
import { actionCopyKey, catchupInfeasibleKey, hppdChangeCopy, moneyChangeCopy, pointsChangeCopy, pointsToneInk } from "./signedCopy.utils";

// The remove control's hover wash — the error hue at a faint alpha, so "remove" previews without shouting.
const REMOVE_HOVER_ALPHA = 0.08;
// The cut scissors sit a notch under the add check, so the two glyphs read as the same optical size.
const CUT_ICON_PX = 16;

type Props = {
	item: PlanItem;
	// This item's marginal points over the items added before it (the cumulative pass).
	itemResult: CumulativeItemResult;
	periodMode: PeriodMode;
	// Projected quarter dollars (`pricedSpend × census × applicable days`) — signed, negative = savings.
	price: number;
	// The per-CALENDAR-day PPD over the window the period mode measures — signed like `price`.
	displayPpd: number;
	// Calendar days the weekend-spend tooltip normalizes over in this period mode.
	ppdWindowDays: number;
	quarterWeekendDays: number;
	// Remaining applicable days for this item (weekend days for a weekend lever) — picks the
	// "catch-up not feasible" reason.
	applicableDaysRemaining: number;
	onRemove: (id: PlanItem["id"]) => void;
};

/**
 * One row of the Required Actions card: the "Schedule / Cut N hours per week" line, its lever + HPPD
 * change (with a per-measure breakdown when it moves more than one), and on the right its marginal
 * points, PPD and projected dollars with a remove control. Cuts wear the savings hue + scissors so a
 * mixed plan scans as "adds" vs "cuts" before any text is read.
 */
export const ActionItemRow: FC<Props> = ({
	item,
	itemResult,
	periodMode,
	price,
	displayPpd,
	ppdWindowDays,
	quarterWeekendDays,
	applicableDaysRemaining,
	onRemove,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const savingsInk = theme.palette.success.main;
	const isWeekend = WEEKEND_REC_IDS.has(item.id);
	// A cut (negative spend — its sign survives even when the hours round to 0) reads in savings terms.
	const isCut = item.spend < 0;
	// In "Quarter to date" mode the change is delivered only over the REMAINING applicable days, so the
	// per-day HPPD bump in that window is higher by `hoursCatchup / hours`. Sign-safe: a cut's catch-up
	// and sustained rates are both negative, so the ratio stays positive.
	const isCatchupMode = periodMode === "qtd" && item.hoursCatchup !== null;
	const hppdMultiplier = isCatchupMode && item.hoursCatchup !== null && item.hours !== 0 ? item.hoursCatchup / item.hours : 1;
	// Per-week hours: weekend items move hours on Sat+Sun only; every-day items on all 7.
	const daysPerWeek = isWeekend ? DAYS_PER_WEEK_WEEKEND : DAYS_PER_WEEK_FULL;
	const hoursPerWeek = periodMode === "qtd" && item.hoursCatchup !== null ? item.hoursCatchup * daysPerWeek : item.hours * daysPerWeek;
	const moneyCopy = moneyChangeCopy(price, isCut);
	const pointsCopy = pointsChangeCopy(itemResult.points, isCut);
	const impactCopy = hppdChangeCopy(item.impact * hppdMultiplier);

	return (
		<Stack direction="row" sx={{ p: 2, alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }} data-testid={`pbj-model-action-item-${item.id}`}>
			<Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start", minWidth: 0 }}>
				<AccentIconTile size="xs" sx={{ mt: 0.25 }} {...(isCut ? { color: savingsInk, tint: alpha(savingsInk, PBJ_ALPHA.TINT) } : {})}>
					{isCut ? <ContentCutRoundedIcon sx={{ fontSize: CUT_ICON_PX }} /> : <CheckCircleIcon sx={{ fontSize: ICON_SIZE.INLINE }} />}
				</AccentIconTile>
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="body2" sx={{ fontWeight: "bold" }}>
						{periodMode === "qtd" && item.hoursCatchup === null
							? // No catch-up rate: no applicable days remain (add), or the catch-up cut would pass the
								// lever's cut limit (cut).
								t(catchupInfeasibleKey(item.spend, applicableDaysRemaining))
							: // Per-week hours as a magnitude — the copy carries "Cut" vs "Schedule".
								t(actionCopyKey(item, periodMode), { hours: Math.abs(hoursPerWeek) })}
					</Typography>
					<Typography variant="caption" sx={{ color: "text.secondary" }}>
						{t(item.titleKey)} • {t(impactCopy.key, { value: impactCopy.magnitude.toFixed(3) })}
					</Typography>
					{item.breakdown && item.breakdown.length > 1 && (
						<Stack
							spacing={0.25}
							sx={{ mt: 0.75, pl: 1, borderLeft: "2px solid", borderLeftColor: "divider" }}
							data-testid={`pbj-model-action-breakdown-${item.id}`}
						>
							{item.breakdown.map((b) => {
								// DEFAULT: a measure missing from this prediction (N/A) is never scored by the pass, so
								// it moved no points.
								const catPoints = itemResult.pointsByCategory[b.categoryId] ?? 0;
								const catGapCopy = hppdChangeCopy(b.hppdGap * hppdMultiplier);
								const catPointsCopy = pointsChangeCopy(catPoints, isCut);
								return (
									<Typography key={b.categoryId} variant="caption" sx={{ display: "block", color: "text.secondary" }}>
										{b.categoryTitle}: {t(catGapCopy.key, { value: catGapCopy.magnitude.toFixed(3) })}
										{" • "}
										<Box
											component="span"
											sx={{ fontWeight: 600, color: catPointsCopy.tone === "none" ? "text.secondary" : pointsToneInk(theme, catPointsCopy.tone, accent) }}
										>
											{t(catPointsCopy.key, catPointsCopy.values)}
										</Box>
									</Typography>
								);
							})}
						</Stack>
					)}
				</Box>
			</Stack>
			<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexShrink: 0 }}>
				<Box sx={{ textAlign: "right" }}>
					<Typography
						variant="body2"
						sx={{ fontWeight: "bold", color: pointsToneInk(theme, pointsCopy.tone, accent) }}
						data-testid={`pbj-model-action-item-points-${item.id}`}
						data-points-tone={pointsCopy.tone}
					>
						{t(pointsCopy.key, pointsCopy.values)}
					</Typography>
					{/* Per-item PPD, normalized to a per-CALENDAR-day rate over the window the period mode
					    measures. The tooltip shows the raw $/weekend-PPD the Recommendations slider controls. */}
					<InHouseTooltip
						arrow
						placement="left"
						title={
							isWeekend
								? t("pbj_model.action_plan.weekend_spend_tooltip", {
										value: Math.abs(item.pricedSpend).toFixed(2),
										weekendDays: quarterWeekendDays,
										quarterDays: ppdWindowDays,
									})
								: ""
						}
					>
						{/* A cut's PPD is savings, so it says so — a bare "$4.63" beside a cut reads as spend. */}
						<Typography
							variant="caption"
							sx={{ color: "text.secondary", cursor: isWeekend ? "help" : "default" }}
							data-testid={`pbj-model-action-item-ppd-${item.id}`}
						>
							{t(isCut ? "pbj_model.action_plan.item_ppd_savings" : "pbj_model.recommendations.spend_format", { value: Math.abs(displayPpd).toFixed(2) })}
						</Typography>
					</InHouseTooltip>
					<Typography
						variant="caption"
						sx={{ display: "block", fontWeight: 600, color: moneyCopy.isSavings ? savingsInk : "text.secondary" }}
						data-testid={`pbj-model-action-item-cost-${item.id}`}
					>
						{t(moneyCopy.key, { value: moneyCopy.value })}
					</Typography>
				</Box>
				<IconButton
					size="small"
					onClick={() => onRemove(item.id)}
					sx={{ color: "text.secondary", "&:hover": { color: "error.main", bgcolor: alpha(theme.palette.error.main, REMOVE_HOVER_ALPHA) } }}
					aria-label={t("pbj_model.action_plan.remove")}
					data-testid={`pbj-model-action-remove-${item.id}`}
				>
					<CloseIcon sx={{ fontSize: ICON_SIZE.INLINE }} />
				</IconButton>
			</Stack>
		</Stack>
	);
};
